import {
    Component,
    ElementRef,
    OnDestroy,
    QueryList,
    ViewChildren,
    ChangeDetectionStrategy,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { EChartsOption } from 'echarts';
import moment from 'moment';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import { NUMBER_OF_DAYS_TO_START_EXPIRING } from '../../shared/utils/constants';
import {
    getFilenameFromDateRange,
    isBeforeNDaysFromTheDate,
    isInThePast,
} from '../../shared/utils/utils';
import { AppState } from '../../store';
import { $selectedCompanyName } from '../../store/companies/companies.selectors';
import {
    getSiteAlertsRequestAction,
    setAlertsOnly,
    setIsReport,
} from '../../store/monitoring/monitoring.actions';
import {
    $chartOptions,
    $currentLicense,
    $dashboardSingleStatData,
    $dashboardStackedChartData,
    $selectedSiteName,
    TableOptions,
    $sitePendingAlerts,
} from '../../store/sites/sites.selectors';
import {
    $selectedPeriod,
    $siteReportMetrics,
    $reportMetricsAllLoaded,
} from '../../store/monitoring/monitoring.selectors';
import {
    DashboardSingleStatValueModel,
    DashboardRatioBarValueModel,
} from '../../shared/models/dashboard-value.model';
import { AlertInterval } from '@myrtls/api-interfaces';

/** One printed page of the System Overview section. */
export interface OverviewPage {
    bars: DashboardRatioBarValueModel[];
    stats: DashboardSingleStatValueModel[];
}

/*
 * Heights of the overview building blocks, measured in the rendered report at
 * A4 landscape (1122x794). The section used to be a single fixed page, so with
 * every metric enabled it ran ~54px past the page and spilled a sliver onto a
 * second page that had no header or footer.
 */
const PAGE_HEIGHT = 794;
const PAGE_CHROME = 41 + 51 + 64; // header + footer + vertical padding
/** Leave a little slack so a slightly taller label cannot re-introduce a spill. */
const PAGE_CONTENT_BUDGET = PAGE_HEIGHT - PAGE_CHROME - 20;
const RATIO_BAR_HEIGHT = 96;
const STATS_HEADER_HEIGHT = 34;
const STATS_ROW_HEIGHT = 80;
const STATS_PER_ROW = 3;
/** Metric tables: a sticky-less header row plus `.mat-row` at 30px each. */
const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 30;
/**
 * Shared instance for metrics with no table rows. `tablePages` is called from
 * the template, so it must return the same reference every time or `*ngFor`
 * re-renders on each change detection pass and the view never settles.
 */
const SINGLE_EMPTY_PAGE: unknown[][] = [[]];

@Component({
    selector: 'myrtls-report',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ReportComponent implements OnDestroy {
    columns: string[] = ['metric', 'metadata', 'from'];

    private sumTables = 0;
    public pagesCount = 0;
    public paginatedData: AlertInterval[][] = [];

    /** System Overview split so each page fits, mirroring Pending Alerts. */
    public overviewPages: OverviewPage[] = [];

    private readonly unsubscribe$ = new Subject<void>();

    private allDataLoaded = false;
    private expectedRenderCount = 0;

    readonly pendingAlerts$: Observable<AlertInterval[] | null> =
        this.store.pipe(select($sitePendingAlerts));

    readonly singleStatWidgetData$: Observable<
        DashboardSingleStatValueModel[]
    > = this.store.pipe(select($dashboardSingleStatData));

    readonly stackedChartData$: Observable<DashboardRatioBarValueModel[]> =
        this.store.pipe(select($dashboardStackedChartData));

    readonly siteReportMetrics$ = this.store.pipe(select($siteReportMetrics));
    readonly selectedSiteName$ = this.store.pipe(select($selectedSiteName));
    readonly selectedCompanyName$ = this.store.pipe(
        select($selectedCompanyName),
    );

    readonly currentLicense$ = this.store.select($currentLicense);

    renderedCharts: string[] = [];
    downloadedMetrics: (string | false)[] = [];

    readonly selectedPeriod$ = this.store.pipe(select($selectedPeriod));
    readonly chartOptions$ = this.store.pipe(select($chartOptions(false)));
    readonly reportMetricsAllLoaded$ = this.store.select(
        $reportMetricsAllLoaded,
    );

    readonly getFilenameFromDateRange = getFilenameFromDateRange;

    metricsLoaded = false;
    alertsLoaded = false;

    Array = Array;

    @ViewChildren('table') set tables(
        content: QueryList<ElementRef<HTMLDivElement>>,
    ) {
        for (const element of content) {
            const metricId = element.nativeElement.getAttribute('metricId');
            if (!metricId) {
                continue;
            }
            if (!this.renderedCharts.includes(metricId)) {
                this.renderedCharts.push(metricId);
            }
        }
        this.checkAllLoaded();
    }

    constructor(private readonly store: Store<AppState>) {
        this.store.dispatch(setAlertsOnly({ alertsOnly: true }));
        this.store.dispatch(setIsReport({ isReport: true }));
        this.store.dispatch(getSiteAlertsRequestAction());

        this.pendingAlerts$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(data => {
                if (data === null) {
                    return;
                }

                this.makeChunksOfData(data, 10);
                this.alertsLoaded = true;
            });

        combineLatest([this.stackedChartData$, this.singleStatWidgetData$])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([bars, stats]) => {
                this.overviewPages = this.paginateOverview(
                    bars ?? [],
                    stats ?? [],
                );
            });

        combineLatest([
            this.reportMetricsAllLoaded$,
            this.chartOptions$,
            this.siteReportMetrics$,
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(([allLoaded, options, reportMetrics]) => {
                this.allDataLoaded = allLoaded;
                this.expectedRenderCount = options.filter(
                    m =>
                        !!m && reportMetrics.includes((m as { id: string }).id),
                ).length;
                this.checkAllLoaded();
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /**
     * Splits the overview into pages that each fit within one printed page.
     *
     * Ratio bars are placed first, then the single-stat tiles. If the tiles do
     * not all fit alongside the bars but would fit on a page of their own, they
     * move across together rather than leaving one or two orphaned on a page.
     */
    private paginateOverview(
        bars: DashboardRatioBarValueModel[],
        stats: DashboardSingleStatValueModel[],
    ): OverviewPage[] {
        if (!bars.length && !stats.length) {
            return [];
        }

        const statsHeight = (count: number) =>
            count === 0
                ? 0
                : STATS_HEADER_HEIGHT +
                  Math.ceil(count / STATS_PER_ROW) * STATS_ROW_HEIGHT;

        const maxStatsIn = (available: number) => {
            const rows = Math.floor(
                (available - STATS_HEADER_HEIGHT) / STATS_ROW_HEIGHT,
            );

            return rows > 0 ? rows * STATS_PER_ROW : 0;
        };

        const pages: OverviewPage[] = [];
        let current: OverviewPage = { bars: [], stats: [] };
        let used = 0;

        for (const bar of bars) {
            if (used + RATIO_BAR_HEIGHT > PAGE_CONTENT_BUDGET && used > 0) {
                pages.push(current);
                current = { bars: [], stats: [] };
                used = 0;
            }

            current.bars.push(bar);
            used += RATIO_BAR_HEIGHT;
        }

        let remaining = [...stats];

        // Keep the tiles together when they would fit on their own page.
        if (
            remaining.length &&
            used > 0 &&
            statsHeight(remaining.length) > PAGE_CONTENT_BUDGET - used &&
            statsHeight(remaining.length) <= PAGE_CONTENT_BUDGET
        ) {
            pages.push(current);
            current = { bars: [], stats: [] };
            used = 0;
        }

        while (remaining.length) {
            const capacity = maxStatsIn(PAGE_CONTENT_BUDGET - used);

            if (capacity === 0) {
                pages.push(current);
                current = { bars: [], stats: [] };
                used = 0;
                continue;
            }

            current.stats.push(...remaining.slice(0, capacity));
            remaining = remaining.slice(capacity);
            used += statsHeight(current.stats.length);

            if (remaining.length) {
                pages.push(current);
                current = { bars: [], stats: [] };
                used = 0;
            }
        }

        if (current.bars.length || current.stats.length) {
            pages.push(current);
        }

        return pages;
    }

    makeChunksOfData(data: AlertInterval[], size: number) {
        this.paginatedData = [];

        for (let i = 0; i < data.length; i += size) {
            const chunk = data.slice(i, i + size);
            this.paginatedData.push(chunk);
        }

        this.pagesCount = this.paginatedData.length;
    }

    onChartRendered(metricId: string) {
        if (!this.renderedCharts.includes(metricId)) {
            this.renderedCharts.push(metricId);
        }
        this.checkAllLoaded();
    }

    private checkAllLoaded(): void {
        if (
            this.allDataLoaded &&
            this.renderedCharts.length >= this.expectedRenderCount
        ) {
            this.metricsLoaded = true;
        }
    }

    metricById(_index: number, metric: { id: string } | null | false) {
        return metric ? metric.id : undefined;
    }

    private readonly tablePageCache = new Map<string, unknown[][]>();

    /**
     * Splits a metric's table across printed pages.
     *
     * Each metric section is one fixed-height page, so a table longer than the
     * page used to run past it and continue on the next sheet with no header,
     * no date and no footer, cut mid-row. The chart keeps the first page, which
     * leaves less room for rows there than on the pages that follow.
     */
    tablePages(metric: {
        id: string;
        options?: EChartsOption;
        tableOptions?: { rows: unknown[] };
    }): unknown[][] {
        const rows = metric.tableOptions?.rows ?? [];

        if (!rows.length) {
            // Still one page: the chart, or the table's own "no data" row.
            return SINGLE_EMPTY_PAGE;
        }

        const key = `${metric.id}:${rows.length}`;
        const cached = this.tablePageCache.get(key);

        if (cached) {
            return cached;
        }

        const chartHeight = metric.options
            ? parseInt(this.getChartHeight(metric.id, metric.options), 10)
            : 0;

        const capacity = (available: number) =>
            Math.max(
                1,
                Math.floor(
                    (available - TABLE_HEADER_HEIGHT) / TABLE_ROW_HEIGHT,
                ),
            );

        const firstPage = capacity(PAGE_CONTENT_BUDGET - chartHeight);
        const laterPages = capacity(PAGE_CONTENT_BUDGET);

        const pages: unknown[][] = [rows.slice(0, firstPage)];

        for (let index = firstPage; index < rows.length; index += laterPages) {
            pages.push(rows.slice(index, index + laterPages));
        }

        this.tablePageCache.set(key, pages);

        return pages;
    }

    getChartHeight(metric: string, options: EChartsOption) {
        if (
            !Array.isArray(options.legend) &&
            metric === 'master-states' &&
            options.legend?.data?.length
        ) {
            return options.legend.data.length * 20 + 80 + 'px';
        }

        return '400px';
    }

    getColumns(options: TableOptions) {
        return options.columns.map(column => column.name);
    }

    getCurrentDate() {
        return moment().format('YYYY/MM/DD HH:mm');
    }

    isExpiring(expiration: number) {
        if (
            isBeforeNDaysFromTheDate(
                expiration,
                NUMBER_OF_DAYS_TO_START_EXPIRING,
            ) ||
            isInThePast(expiration)
        ) {
            return false;
        }

        return true;
    }
}
